import { filter, groupBy, sortBy } from 'lodash/collection';
import moment from 'moment';

import { CREDIT_MEMO, DEBIT_MEMO } from 'components/pages/finance/NewInvoice/InvoiceTable/TableConstants';
import {
  getGroupAmount,
  getGroupLength,
  getGroupOpenBalance,
  getSubGroupAmount,
  getSubGroupOpenBalance,
  isAdjustmentEventType,
  isParentEventType,
  isWithholdingEventType
} from 'components/pages/finance/NewInvoice/services/service';

import { toFormattedFinanceBill } from '../../../../../../services/financial';
import { reimbursementItemTypesMap } from '../../../../patient-source/Patients/PatientInfo/EncountersSection/PaymentsSection/reimbursementConstants';
import { eventTypeLabel } from '../../../../setup/Budget/BudgetDetails/budgetConstant';

function mapAllParentsThatHasAdjustments(events) {
  // Pre-scan: mark PARENT events that has adjustments
  const adjustmentEventNumbers = events
    .filter(e => isAdjustmentEventType(e.invoiceType))
    .map(({ eventNumber }) => eventNumber);

  events.forEach(event => {
    if (isAdjustmentEventType(event.invoiceType) && !adjustmentEventNumbers.includes(event.eventNumber)) {
      event.hasAdjustments = true;
    }
  });
}

function resolveAdjustmentDisplayName(event) {
  switch (event.invoiceType) {
    case CREDIT_MEMO:
      return 'CM';
    case DEBIT_MEMO:
      return 'DM';
    default:
      return event.displayName;
  }
}

/**
 * Stage 1 — Computes `openBalance`, `openBalanceString`, and `rate` as flat properties
 * on each supported event (PARENT, CREDIT_MEMO, DEBIT_MEMO, WITHHOLDING).
 * Non-supported event types are returned unchanged.
 *
 * For adjustment events (CM/DM) whose parent event is not present in the
 * `events` list (orphan adjustments — the old case when a parent invoice was
 * created without a PDF file), `rate` and `openBalance` fall back to
 * `invoiceAmount` instead of `amountWithOverhead` / `clientAmountWithHolding`.
 *
 * When `includeAdjustments` is `true` and the parent event is present,
 * a `displayName` property is set on adjustment events:
 *  - `CREDIT_MEMO` events get `displayName = "CM"`
 *  - `DEBIT_MEMO`  events get `displayName = "DM"`
 *
 * For all other event types `displayName` is not modified and falls back to
 * the original `name` field when rendered.
 */
export const computeCalculatedValues = (events, includeAdjustments) => {
  mapAllParentsThatHasAdjustments(events);

  return events.map(event => {
    const { invoiceType } = event;

    const isSupportedType =
      isParentEventType(invoiceType) || isAdjustmentEventType(invoiceType) || isWithholdingEventType(invoiceType);

    if (!isSupportedType) {
      return event;
    }

    const hasParentInList =
      !isParentEventType(invoiceType) &&
      events.some(e => e.eventNumber === event.eventNumber && isParentEventType(e.invoiceType));

    const isOrphanAdjustment = !isParentEventType(invoiceType) && !hasParentInList;

    const paid = event.reconcileTotalAmountPaid;

    const rate = isOrphanAdjustment
      ? toFormattedFinanceBill(event.invoiceAmount)
      : toFormattedFinanceBill(includeAdjustments ? event.amountWithOverhead : event.invoiceAmount);

    const openBalance = isOrphanAdjustment
      ? event.invoiceAmount - paid
      : (includeAdjustments ? event.amountWithOverhead : event.clientAmountWithHolding) - paid;

    const displayName = includeAdjustments && hasParentInList ? resolveAdjustmentDisplayName(event) : event.displayName;

    return {
      ...event,
      displayName,
      openBalance,
      openBalanceString: toFormattedFinanceBill(openBalance),
      rate
    };
  });
};

/**
 * Stage 2 — Assigns `groupName`, `subGroupName`, and display-related properties
 * to each budget event based on encounter type.
 *
 * **`subGroupName`** is always computed as:
 *   `"Subject ID <subjectNumber>"` or `"Patient ID <patientId>"`, followed by
 *   the formatted `encounterDate` `(DD-MMM-YYYY)` or `"—"` when absent.
 *
 * **`groupName`** is derived according to encounter type:
 *  - `UNEXPECTED` — `"Unscheduled <encounterName>"` (strips leading `"Unexpected:"` prefix)
 *  - `SITUATIONAL` — `"Situational <encounterName>"` (strips leading `"Situational:"` prefix)
 *  - Normal (has encounter) — `"<epochName>-<encounterName>"`
 *  - No encounter (fixed/variable events) — `"<name>: Rate <invoiceAmount>"`
 *
 * **Additional properties set for no-encounter events** (`hasEncounter === false`):
 *  - `groupRateForFixedAndVariableEvents` — formatted `invoiceAmount`
 *  - `groupNameForFixedAndVariableEvents` — raw event `name`
 *
 * **`displayName`** — for encounter events `groupNameForDisplay` equals `groupName`.
 *
 * When `includeAdjustments` is `true`, `groupName` gains an `" (With Adjustment) <eventNumber>"`
 * suffix in two cases:
 *  - The event's `invoiceAmount` differs from `clientAmount` (sponsor-side adjustment)
 *  - The event's `invoiceType` is `CREDIT_MEMO` or `DEBIT_MEMO`
 *
 * `subGroupName` is set to `null` for no-encounter events so that Stage 5
 * skips sub-group header generation for those rows.
 */
export const assignGroupAndSubGroupNames = events => {
  return events.map(budgetEvent => {
    const subGroupName = `${
      budgetEvent?.subjectNumber ? `Subject ID ${budgetEvent.subjectNumber}` : `Patient ID ${budgetEvent.patientId}`
    } ${budgetEvent?.encounterDate ? `(${moment(budgetEvent.encounterDate).format('DD-MMM-YYYY')})` : '—'}`;

    if (budgetEvent.encounterType === 'UNEXPECTED') {
      const groupName = `Unscheduled ${budgetEvent.encounterName?.replace(/^Unexpected:/, '') || '—'}`;
      return { ...budgetEvent, groupName, subGroupName };
    }
    if (budgetEvent.encounterType === 'SITUATIONAL') {
      const groupName = `Situational ${budgetEvent.encounterName?.replace(/^Situational:/, '') || '—'}`;
      return { ...budgetEvent, groupName, subGroupName };
    }

    const hasEncounter = !!budgetEvent.encounterName;

    let groupName;

    if (hasEncounter) {
      groupName = `${budgetEvent?.epochName || '—'}-${budgetEvent?.encounterName}`;
    } else {
      if (isAdjustmentEventType(budgetEvent.invoiceType) || budgetEvent.hasAdjustments) {
        groupName = `${budgetEvent.name || ''} ${budgetEvent.eventNumber}`;
      } else {
        groupName = `${budgetEvent?.name || ''}: Rate ${toFormattedFinanceBill(budgetEvent.invoiceAmount)}`;
      }
    }

    const addThis = {};

    if (!hasEncounter) {
      addThis.groupRateForFixedAndVariableEvents = toFormattedFinanceBill(budgetEvent.invoiceAmount);
      addThis.groupNameForFixedAndVariableEvents = `${budgetEvent?.name || ''}`;
    }

    return {
      ...budgetEvent,
      groupName,
      ...addThis,
      hasEncounter,
      subGroupName: hasEncounter ? subGroupName : null
    };
  });
};

/**
 * Stage 3 — Sorts events by 5 keys:
 * 1. Non-encounter events after encounter events
 * 2. groupName (case-insensitive)
 * 3. subGroupName (case-insensitive)
 * 4. subjectNumber (case-insensitive)
 * 5. name (case-insensitive)
 * 6. adjustmentSequence (to keep adjustments in original order at the end of each group)
 */
export const sortEvents = events => {
  return sortBy(events, [
    el => !el.encounterName,
    el => el.groupName?.toLowerCase(),
    el => el.subGroupName?.toLowerCase(),
    el => el.subjectNumber?.toLowerCase(),
    el => el.name?.toLowerCase(),
    el => el.adjustmentSequence
  ]);
};

const STIPEND_ZERO_TOLERANCE = 0.000001;
const PATIENT_STIPEND_LABEL = eventTypeLabel.ITEM_GROUP_PATIENT_STIPEND;

const buildStipendIdentityKey = event => {
  return [event.eventNumber ?? event.id ?? '', event.subjectNumber ?? '', event.patientId ?? ''].join('|');
};

const buildStipendGroupKey = event => buildStipendIdentityKey(event);

const isZeroAmount = amount => {
  const normalizedAmount = Number(amount ?? 0);
  return Math.abs(normalizedAmount) < STIPEND_ZERO_TOLERANCE;
};

const resolveStipendAmountForTotal = (event, includeAdjustments) => {
  const adjustmentAwareAmount = includeAdjustments ? event.amountWithOverhead : undefined;
  const normalizedAmount = Number(adjustmentAwareAmount ?? event.invoiceAmount ?? 0);

  return Number.isNaN(normalizedAmount) ? 0 : normalizedAmount;
};

/**
 * Stage 4 — Removes:
 * 1) standalone Patient Stipend rows with zero invoiceAmount
 * 2) Patient Stipend parent + adjustment groups (CM/DM) that net to zero
 *    based on display amount in adjustment mode (`amountWithOverhead`) and
 *    on `invoiceAmount` otherwise.
 */
export const filterZeroStipends = (events, { includeAdjustments = false } = {}) => {
  const stipendGroups = events.reduce((acc, event) => {
    if (event.budgetEventType !== PATIENT_STIPEND_LABEL) {
      return acc;
    }

    const key = buildStipendGroupKey(event);
    const prev = acc[key] ?? { total: 0, hasParent: false, hasAdjustments: false };

    acc[key] = {
      total: prev.total + resolveStipendAmountForTotal(event, includeAdjustments),
      hasParent: prev.hasParent || isParentEventType(event.invoiceType),
      hasAdjustments: prev.hasAdjustments || isAdjustmentEventType(event.invoiceType)
    };

    return acc;
  }, {});

  return filter(events, event => {
    if (event.budgetEventType !== PATIENT_STIPEND_LABEL) {
      return true;
    }

    const group = stipendGroups[buildStipendGroupKey(event)];
    const isZeroTotalStipendAdjustmentGroup = group?.hasParent && group?.hasAdjustments && isZeroAmount(group.total);

    if (isZeroTotalStipendAdjustmentGroup) {
      return false;
    }

    return !isZeroAmount(event.invoiceAmount);
  });
};

/**
 * Stage 5 — Groups events by groupName and builds a flattened structure
 * with typed markers (typeHeader, typeSubHeader, typeSubItem).
 */
export const buildGroupedStructure = (events, invoiceType) => {
  const grouped = groupBy(events, ({ groupName, hasAdjustments, eventNumber, invoiceType }) => {
    const isParentWithAdjustments = isParentEventType(invoiceType) && hasAdjustments;

    if (isParentWithAdjustments || isAdjustmentEventType(invoiceType)) {
      return `${groupName} - ${eventNumber}`;
    }

    return groupName;
  });
  return Object.values(grouped).reduce((acc, group) => {
    let groupWithSubGroup;
    if (group[0].subGroupName) {
      const groupedBySubGroup = groupBy(group, ({ subGroupName }) => subGroupName);
      groupWithSubGroup = Object.values(groupedBySubGroup).reduce((acc, subGroup) => {
        return [
          ...acc,
          {
            typeSubHeader: true,
            groupName: group[0].groupName,
            subGroupName: subGroup[0].subGroupName,
            subGroupAmount: getSubGroupAmount(subGroup),
            subGroupOpenBalance: getSubGroupOpenBalance(subGroup)
          },
          ...subGroup.map(item => ({ ...item, typeSubItem: true }))
        ];
      }, []);
    } else {
      groupWithSubGroup = group;
    }

    const groupLength = getGroupLength(groupWithSubGroup, invoiceType);
    const groupAmount = getGroupAmount(groupWithSubGroup);
    const groupOpenBalance = getGroupOpenBalance(groupWithSubGroup);
    const groupOpenBalanceString = toFormattedFinanceBill(groupOpenBalance);
    const groupRateForFixedAndVariableEvents = group[0].groupRateForFixedAndVariableEvents;
    const groupNameForFixedAndVariableEvents = group[0].groupNameForFixedAndVariableEvents;

    return [
      ...acc,
      {
        typeHeader: true,
        inseparable: !!group[0].subGroupName,
        groupName: group[0].groupName,
        groupLength,
        groupAmount,
        groupOpenBalance,
        groupOpenBalanceString,
        groupRateForFixedAndVariableEvents,
        groupNameForFixedAndVariableEvents,
        amountForItem: group[0].invoiceAmount,
        headerBudgetEventType: group[0].budgetEventType
      },
      ...groupWithSubGroup
    ];
  }, []);
};

/**
 * Stage 6 — Expands Patient Reimbursement events by flattening
 * their reimbursementItems into individual REIMBURSEMENT_ITEM rows.
 */
export const expandReimbursementItems = items => {
  return items.flatMap(el => {
    if (el.budgetEventType === eventTypeLabel.PATIENT_REIMBURSEMENT) {
      return [
        el,
        ...el.reimbursementItems.map(reimbursement => ({
          ...reimbursement,
          templateRowType: 'REIMBURSEMENT_ITEM',
          groupName: el.groupName,
          type: reimbursementItemTypesMap[reimbursement.type],
          ...el
        }))
      ];
    }
    return el;
  });
};

/**
 * Full normalization pipeline — composes all stages and appends balance row.
 */
export const normalizeInvoiceGroups = (
  invoices,
  { comment, balanceDue, paymentsCredits, includeAdjustments, invoiceType }
) => {
  const withCalculatedValues = computeCalculatedValues(invoices, includeAdjustments);

  const withNames = assignGroupAndSubGroupNames(withCalculatedValues);

  const sorted = sortEvents(withNames);

  const filtered = filterZeroStipends(sorted, { includeAdjustments });

  const grouped = buildGroupedStructure(filtered, invoiceType);

  const expanded = expandReimbursementItems(grouped);

  return [...expanded, { typeBalance: true, comment, balanceDue, paymentsCredits }];
};
